import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import AdminLayout from "./components/Admin/AdminLayout";
import SuperadminLayout from "./components/Superadmin/SuperadminLayout";
import TeacherLayout from "./components/Teacher/TeacherLayout";
import StudentLayout from "./components/Student/StudentLayout";
import ParentLayout from "./components/Parent/ParentLayout";
import DeveloperLayout from "./components/Developer/DeveloperLayout";
import Dashboard from "./pages/Dashboard";
import Grades from "./pages/Grades";
import Feedback from "./pages/Feedback";
import AcademicEyePilot from "./pages/AcademicEyePilot";
import Login from "./pages/login";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Forbidden from "./pages/Forbidden";
import ChangePassword from "./pages/ChangePassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FeedbackPopup from "./components/FeedbackPopup";

// ProtectedRoute component for role-based access
function ProtectedRoute({ children, allowedRoles }: any) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles) {
    // Check both user.role (string) and user.roles (array)
    const hasRole = allowedRoles.includes(user.role) || (Array.isArray(user.roles) && user.roles.some(r => allowedRoles.includes(r)));
    if (!hasRole) {
      return <Forbidden />;
    }
  }
  return children;
}

function getViewportState() {
  if (typeof window === "undefined") {
    return { mode: "desktop", isCompact: false, isStandalone: false };
  }

  const width = window.innerWidth;
  const isCompact = width <= 860;
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || Boolean(window.navigator.standalone);
  const mode = width <= 560 ? "compact" : width <= 1024 ? "tablet" : "desktop";

  return { mode, isCompact, isStandalone };
}

function useResponsiveAppShell() {
  const [viewportState, setViewportState] = useState(() => getViewportState());

  useEffect(() => {
    const updateViewportState = () => setViewportState(getViewportState());

    updateViewportState();
    window.addEventListener("resize", updateViewportState);

    const standaloneMediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleStandaloneChange = () => updateViewportState();

    if (standaloneMediaQuery.addEventListener) {
      standaloneMediaQuery.addEventListener("change", handleStandaloneChange);
    } else {
      standaloneMediaQuery.addListener(handleStandaloneChange);
    }

    return () => {
      window.removeEventListener("resize", updateViewportState);
      if (standaloneMediaQuery.removeEventListener) {
        standaloneMediaQuery.removeEventListener("change", handleStandaloneChange);
      } else {
        standaloneMediaQuery.removeListener(handleStandaloneChange);
      }
    };
  }, []);

  return viewportState;
}

function App() {
  const { mode, isCompact, isStandalone } = useResponsiveAppShell();

  return (
    <div
      className={`ae-app-shell ae-app-shell-${mode}`}
      data-viewport={mode}
      data-compact={isCompact ? "true" : "false"}
      data-standalone={isStandalone ? "true" : "false"}
    >
      <Router>
        <FeedbackPopup>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/request-setup-link" element={<ForgotPassword setupInvitation />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to="/login" />} />

          {/* Force password change — requires auth but no role check */}
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          } />

          {/* Dashboard (all roles) */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={["admin", "school_admin", "district_admin"]}>
              <AdminLayout />
            </ProtectedRoute>
          } />

          {/* Superadmin Routes */}
          <Route path="/superadmin/*" element={
            <ProtectedRoute allowedRoles={["superadmin"]}>
              <SuperadminLayout />
            </ProtectedRoute>
          } />

          {/* Developer Routes (no student data access) */}
          <Route path="/developer/*" element={
            <ProtectedRoute allowedRoles={["developer"]}>
              <DeveloperLayout />
            </ProtectedRoute>
          } />

          {/* Teacher Routes */}
          <Route path="/teacher/*" element={
            <ProtectedRoute allowedRoles={["teacher", "special_services_teacher"]}>
              <TeacherLayout />
            </ProtectedRoute>
          } />

          {/* Student Routes */}
          <Route path="/student/*" element={
            <ProtectedRoute allowedRoles={["student"]}>
              <StudentLayout />
            </ProtectedRoute>
          } />

          {/* Parent Routes */}
          <Route path="/parent/*" element={
            <ProtectedRoute allowedRoles={["parent", "family"]}>
              <ParentLayout />
            </ProtectedRoute>
          } />

          {/* Shared/utility pages */}
          <Route path="/grades" element={
            <ProtectedRoute>
              <Grades />
            </ProtectedRoute>
          } />
          <Route path="/feedback" element={
            <ProtectedRoute>
              <Feedback />
            </ProtectedRoute>
          } />
          <Route path="/pilot" element={
            <ProtectedRoute>
              <AcademicEyePilot />
            </ProtectedRoute>
          } />

          {/* Fallback: redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        </FeedbackPopup>
      </Router>
    </div>
  );
}

export default App;
